import * as Sentry from '@sentry/browser';
import type { CaptureContext } from '@sentry/core';
import type { AxiosError } from 'axios';

interface ErrorWithMessage {
  message: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  );
}

function toErrorWithMessage(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  maybeError: AxiosError<any, any>
): ErrorWithMessage {
  if (
    maybeError.response?.data &&
    isErrorWithMessage(maybeError.response?.data)
  ) {
    return maybeError.response.data;
  }

  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getErrorMessage(error: AxiosError<any, any>): string {
  return toErrorWithMessage(error).message;
}

/**
 * Auth0 refresh-token failures raised by getTokenSilently that mean the
 * session can no longer be renewed silently. These are expected lifecycle
 * events (cleared/evicted token cache, rotated-out or expired refresh token)
 * and should end in a clean redirect to /logout, not a page crash.
 *
 * Matched by Auth0 error code (`error` property on GenericError subclasses)
 * rather than message text, because messages carry variable audience/scope
 * detail — e.g. "Missing Refresh Token (audience: '…', scope: '…')".
 */
export const isRecoverableRefreshTokenError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.message === 'Unknown or invalid refresh token.') {
    return true;
  }
  const code = 'error' in error ? error.error : undefined;

  return code === 'missing_refresh_token' || code === 'invalid_grant';
};

export function handleError(
  error: unknown,
  captureContext?: CaptureContext,
  transactionName?: string
) {
  if (transactionName) {
    Sentry.getCurrentScope().setTransactionName(transactionName);
  }

  Sentry.captureException(error, captureContext);

  console.error(error, captureContext);
}
